import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '../store/authStore'
import type { Notificacao } from './notificacaoService'

// ===== TIPOS DO BACKEND (websocket.types.ts) =====

export const WebSocketEventType = {
  NOTIFICACAO_NOVA: 'notificacao:nova',
  NOTIFICACAO_NOVA_BROADCAST: 'notificacao:nova:broadcast',
  PEDIDO_STATUS_ALTERADO: 'pedido:status_alterado',
  PEDIDO_CRIADO: 'pedido:criado',
  PEDIDO_CANCELADO: 'pedido:cancelado',
  SISTEMA_MENSAGEM: 'sistema:mensagem',
  ERRO: 'error',
} as const

export const MessageSeverity = {
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
} as const

export type WebSocketEventTypeValue = typeof WebSocketEventType[keyof typeof WebSocketEventType]
export type MessageSeverityValue = typeof MessageSeverity[keyof typeof MessageSeverity]

// Interface base para todas as mensagens WebSocket
export interface WebSocketMessage<T = any> {
  event: WebSocketEventTypeValue
  timestamp: string
  severity: MessageSeverityValue
  data: T
  meta?: {
    shouldNotify?: boolean
    shouldToast?: boolean
    autoClose?: number
    actionUrl?: string
    sound?: boolean
  }
}

// Payloads específicos do backend
export interface NotificacaoNovaPayload {
  id: string
  tipo: 'pedido_criado' | 'pedido_atualizado' | 'status_alterado' | 'sistema'
  titulo: string
  mensagem: string
  lida: boolean
  pedidoId?: string
  userId: string
  createdAt: string
}

export interface PedidoStatusAlteradoPayload {
  pedidoId: string
  statusAntigo: string
  statusNovo: string
  alteradoPor: {
    id: string
    name: string
    email: string
  }
  clienteNome: string
  dataAlteracao: string
}

export interface PedidoCriadoPayload {
  pedidoId: string
  clienteId: string
  clienteNome: string
  criadoPor: {
    id: string
    name: string
    email: string
  }
  dataCriacao: string
  valorTotal?: number
  status: string
}

export interface PedidoCanceladoPayload {
  pedidoId: string
  clienteNome: string
  canceladoPor: {
    id: string
    name: string
    email: string
  }
  motivo?: string
  dataCancelamento: string
}

export interface SistemaMensagemPayload {
  titulo: string
  mensagem: string
  tipo: 'manutencao' | 'atualizacao' | 'alerta' | 'info'
}

export interface ErrorPayload {
  code: string
  message: string
  details?: any
}

// ===== FIM DOS TIPOS DO BACKEND =====

type NotificationEventType = 
  | 'notificacao:nova'
  | 'notificacao:nova:broadcast'
  | 'pedido:status_alterado' 
  | 'pedido:criado' 
  | 'pedido:cancelado' 
  | 'sistema:mensagem' 
  | 'error'

type EventListener<T = Notificacao> = (data: T) => void

class WebSocketService {
  private socket: Socket | null = null
  private url: string
  private isConnecting = false

  constructor() {
    // Em produção com nginx, use window.location.origin (mesma origem)
    // Em desenvolvimento local, use a URL do backend
    const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL
    this.url = VITE_API_BASE_URL || window.location.origin
    console.log('🔧 WebSocket URL configurada:', this.url)
  }

  connect() {
    if (this.isConnecting) {
      console.log('⚠️ WebSocket: Conexão já em andamento')
      return
    }

    if (this.socket?.connected) {
      console.log('✅ WebSocket: Já conectado')
      return
    }

    const token = useAuthStore.getState().token
    
    if (!token) {
      console.warn('⚠️ WebSocket: Token não encontrado, não conectando')
      return
    }

    console.log('🔌 WebSocket: Conectando ao Socket.io...', this.url)
    this.isConnecting = true

    try {
      this.socket = io(this.url, {
        query: { token },
         path: '/ws',
        transports: ['websocket'], // Tenta WebSocket primeiro, fallback para polling
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 3000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      })

      // Eventos de conexão
      this.socket.on('connect', () => {
        console.log('✅ WebSocket: Conectado!')
        console.log('📍 Socket ID:', this.socket?.id)
        this.isConnecting = false
      })

      // Listener genérico para DEBUG - captura TODOS os eventos
      this.socket.onAny((eventName, ...args) => {
        console.log('🔍 [DEBUG] Evento recebido:', eventName, args)
      })

      this.socket.on('disconnect', (reason) => {
        console.log('🔌 WebSocket: Desconectado -', reason)
        this.isConnecting = false
        
        if (reason === 'io server disconnect') {
          // Servidor forçou desconexão, tentar reconectar
          console.log('🔄 WebSocket: Tentando reconectar...')
          this.socket?.connect()
        }
      })

      this.socket.on('connect_error', (error) => {
        console.error('❌ WebSocket: Erro de conexão:', error.message)
        this.isConnecting = false
      })

      this.socket.on('reconnect', (attemptNumber) => {
        console.log(`� WebSocket: Reconectado após ${attemptNumber} tentativa(s)`)
      })

      this.socket.on('reconnect_attempt', (attemptNumber) => {
        console.log(`🔄 WebSocket: Tentativa de reconexão ${attemptNumber}...`)
      })

      this.socket.on('reconnect_error', (error) => {
        console.error('❌ WebSocket: Erro ao reconectar:', error.message)
      })

      this.socket.on('reconnect_failed', () => {
        console.error('❌ WebSocket: Falha ao reconectar após múltiplas tentativas')
      })

    } catch (error) {
      console.error('❌ WebSocket: Erro ao criar conexão:', error)
      this.isConnecting = false
    }
  }

  disconnect() {
    console.log('🔌 WebSocket: Desconectando...')
    
    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }
    
    this.isConnecting = false
  }

  // Listener para novas notificações (usuário específico)
  onNovaNotificacao(listener: EventListener<Notificacao>) {
    if (!this.socket) {
      console.warn('⚠️ WebSocket: Socket não conectado, não é possível adicionar listener')
      return
    }

    console.log('👂 WebSocket: Listener adicionado para "notificacao:nova"')
    this.socket.on('notificacao:nova', (message: WebSocketMessage<NotificacaoNovaPayload>) => {
      console.log('📨 WebSocket: Nova notificação recebida:', message)
      
      // Converte payload do backend para formato do frontend
      const notificacao: Notificacao = {
        id: message.data.id,
        userId: message.data.userId,
        tipo: message.data.tipo,
        titulo: message.data.titulo,
        mensagem: message.data.mensagem,
        lida: message.data.lida,
        pedidoId: message.data.pedidoId,
        createdAt: message.data.createdAt,
      }
      
      listener(notificacao)
    })
  }

  // Listener para broadcast de notificações (todos os usuários)
  onNovaNotificacaoBroadcast(listener: EventListener<Notificacao>) {
    if (!this.socket) {
      console.warn('⚠️ WebSocket: Socket não conectado, não é possível adicionar listener')
      return
    }

    console.log('👂 WebSocket: Listener adicionado para "notificacao:nova:broadcast"')
    this.socket.on('notificacao:nova:broadcast', (message: WebSocketMessage<NotificacaoNovaPayload>) => {
      console.log('📢 WebSocket: Notificação broadcast recebida:', message)
      
      // Converte payload do backend para formato do frontend
      const notificacao: Notificacao = {
        id: message.data.id,
        userId: message.data.userId,
        tipo: message.data.tipo,
        titulo: message.data.titulo,
        mensagem: message.data.mensagem,
        lida: message.data.lida,
        pedidoId: message.data.pedidoId,
        createdAt: message.data.createdAt,
      }
      
      listener(notificacao)
    })
  }

  // Listener para status de pedido alterado
  onPedidoStatusAlterado(listener: EventListener<PedidoStatusAlteradoPayload>) {
    if (!this.socket) {
      console.warn('⚠️ WebSocket: Socket não conectado, não é possível adicionar listener')
      return
    }

    console.log('👂 WebSocket: Listener adicionado para "pedido:status_alterado"')
    this.socket.on('pedido:status_alterado', (message: WebSocketMessage<PedidoStatusAlteradoPayload>) => {
      console.log('📦 WebSocket: Status de pedido alterado:', message)
      listener(message.data)
    })
  }

  // Listener para pedido criado
  onPedidoCriado(listener: EventListener<PedidoCriadoPayload>) {
    if (!this.socket) {
      console.warn('⚠️ WebSocket: Socket não conectado, não é possível adicionar listener')
      return
    }

    console.log('👂 WebSocket: Listener adicionado para "pedido:criado"')
    this.socket.on('pedido:criado', (message: WebSocketMessage<PedidoCriadoPayload>) => {
      console.log('🆕 WebSocket: Novo pedido criado:', message)
      listener(message.data)
    })
  }

  // Listener para pedido cancelado
  onPedidoCancelado(listener: EventListener<PedidoCanceladoPayload>) {
    if (!this.socket) {
      console.warn('⚠️ WebSocket: Socket não conectado, não é possível adicionar listener')
      return
    }

    console.log('👂 WebSocket: Listener adicionado para "pedido:cancelado"')
    this.socket.on('pedido:cancelado', (message: WebSocketMessage<PedidoCanceladoPayload>) => {
      console.log('❌ WebSocket: Pedido cancelado:', message)
      listener(message.data)
    })
  }

  // Listener para mensagens do sistema
  onSistemaMensagem(listener: EventListener<SistemaMensagemPayload>) {
    if (!this.socket) {
      console.warn('⚠️ WebSocket: Socket não conectado, não é possível adicionar listener')
      return
    }

    console.log('👂 WebSocket: Listener adicionado para "sistema:mensagem"')
    this.socket.on('sistema:mensagem', (message: WebSocketMessage<SistemaMensagemPayload>) => {
      console.log('📢 WebSocket: Mensagem do sistema:', message)
      listener(message.data)
    })
  }

  // Listener para erros
  onError(listener: EventListener<ErrorPayload>) {
    if (!this.socket) {
      console.warn('⚠️ WebSocket: Socket não conectado, não é possível adicionar listener')
      return
    }

    console.log('👂 WebSocket: Listener adicionado para "error"')
    this.socket.on('error', (message: WebSocketMessage<ErrorPayload>) => {
      console.error('❌ WebSocket: Erro recebido:', message)
      listener(message.data)
    })
  }

  // Remove listener
  off(eventName: NotificationEventType) {
    if (this.socket) {
      this.socket.off(eventName)
      console.log(`👂 WebSocket: Listener removido para "${eventName}"`)
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false
  }

  getSocketId(): string | undefined {
    return this.socket?.id
  }

  // Método para testar eventos manualmente (DEBUG)
  emitTest(eventName: string, data: any) {
    if (this.socket?.connected) {
      console.log(`🧪 [TEST] Emitindo evento "${eventName}":`, data)
      this.socket.emit(eventName, data)
    } else {
      console.warn('⚠️ WebSocket não conectado, não é possível emitir evento')
    }
  }

  // Informações de debug
  getDebugInfo() {
    return {
      connected: this.socket?.connected ?? false,
      socketId: this.socket?.id,
      url: this.url,
      isConnecting: this.isConnecting,
    }
  }
}

// Singleton
export const websocketService = new WebSocketService()

// Expor globalmente para debug no console
if (typeof window !== 'undefined') {
  (window as any).websocketService = websocketService
  console.log('🔧 websocketService disponível globalmente: window.websocketService')
}
